import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from '@inertiajs/react'
import { Button } from '../components/core/Button.jsx'
import { Icon } from '../components/core/Icon.jsx'

// Which business the Overview and the Questions page show. Added 2026-10-11:
// until then both rendered the account's first business and nothing else, so an
// account on Growth or Agency — sized for six and fifteen — could pay for the
// plan and use one business of it.
//
// **A list of links, not a native select that navigates on change.** Arrowing
// through a closed select fires `change` on every step in some browsers, so a
// keyboard user would be sent to each business in turn on the way to the one
// they wanted — a context change on input, which is the WCAG failure the
// pattern is known for. A link is a link: each row says where it goes, opens in
// a new tab when asked, and survives without JavaScript.
//
// Three shapes, decided by the account rather than the page:
//   - more than one business: "Switch business", a list ending in "Add a business"
//   - one, on a plan with room for more: "Add a business" on its own
//   - one, on a plan without room (Starter, or a trial): nothing
// The last is deliberate. "Add a business" is still on the account page for
// everyone, beside the list it would grow; it is only kept off the main screen
// where adding one would pause collection for the business already there.
//
// `path` is the page's own, so switching keeps you on Questions when you were on
// Questions. The choice is remembered server-side — see
// `DashboardController#current_brand` — so the header's links keep it too.
export function BusinessSwitcher({ businesses = [], included = null, path = '/dashboard' }) {
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState(null)
  const wrapper = useRef(null)

  // **Placed by measuring, once, as it opens.** Anchoring to the button with a
  // class cannot work, because where the button sits depends on how long the
  // business's name line is: the header wraps its controls onto their own line
  // at one width and not another. Anchored left, it ran off the right edge on a
  // phone; anchored right from `sm` up, it started 86px off the left edge at
  // 768px, where the controls had wrapped to the left. Measured in a browser
  // both times. So the list takes the button's left edge where it fits and is
  // pulled back inside a 16px gutter where it does not.
  useLayoutEffect(() => {
    if (!open || !wrapper.current) return
    const r = wrapper.current.getBoundingClientRect()
    const gutter = 16
    const width = Math.min(352, window.innerWidth - gutter * 2)
    const left = Math.min(Math.max(r.left, gutter), window.innerWidth - gutter - width)
    setPlace({ left: left - r.left, width })
  }, [open])

  // Escape and a click elsewhere both close it, the way a menu is expected to
  // behave. Registered only while open, so a closed switcher costs nothing.
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    const onDown = (e) => wrapper.current && !wrapper.current.contains(e.target) && setOpen(false)
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open])

  if (businesses.length <= 1) {
    if (!(businesses.length === 1 && included && included > 1)) return null

    return (
      <Button as={Link} href="/brands/new" variant="ghost" size="sm" iconLeft="plus">
        Add a business
      </Button>
    )
  }

  return (
    <div ref={wrapper} className="relative">
      <Button
        as="button"
        type="button"
        variant="ghost"
        size="sm"
        iconLeft="layers"
        aria-expanded={open}
        aria-controls="business-switcher"
        onClick={() => setOpen((v) => !v)}
      >
        Switch business
      </Button>
      {open && (
        // **Two elements, and that is the fix rather than tidiness.** `.sp-card`
        // sets `position: relative` in the design system's unlayered CSS, which
        // outranks Tailwind's `absolute` whatever the specificity — the
        // cascade-layer landmine in CLAUDE.md. On one element the list sat in the
        // page's flow and pushed everything below it down. The outer element
        // positions; the card inside is free to be relative.
        //
        // Hidden for the one frame before it is measured, so it never flashes
        // at the wrong place.
        <div
          id="business-switcher"
          className="absolute top-full z-20 mt-2"
          style={place ? { left: place.left, width: place.width } : { visibility: 'hidden' }}
        >
          <div className="sp-card sp-card--flush overflow-hidden">
            <ul className="flex flex-col">
              {businesses.map((b) => (
                <li key={b.id}>
                  {/* Every row a link, the current one included, so the list
                      reads the same whichever business is showing. */}
                  <Link
                    href={`${path}?brand=${b.id}`}
                    aria-current={b.current ? 'true' : undefined}
                    onClick={() => setOpen(false)}
                    className="flex min-h-[44px] items-center justify-between gap-3 px-4 py-2 hover:bg-[color:var(--surface-hover)]"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-[14px] text-[color:var(--text-strong)]">{b.name}</span>
                      {(b.city || b.state) && (
                        <span className="truncate text-[12.5px] text-[color:var(--text-muted)]">
                          {[b.city, b.state].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </span>
                    {/* Decorative — `Icon` is always aria-hidden. The row's
                        `aria-current` is what a screen reader announces. */}
                    {b.current && <Icon name="check" size={16} />}
                  </Link>
                </li>
              ))}
              <li className="border-t border-[color:var(--border-subtle)]">
                <Link
                  href="/brands/new"
                  onClick={() => setOpen(false)}
                  className="flex min-h-[44px] items-center gap-2 px-4 text-[14px] hover:bg-[color:var(--surface-hover)]"
                >
                  <Icon name="plus" size={16} />
                  Add a business
                </Link>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
